import { useState } from 'react'
import { Button, Input, Select, Textarea } from '@/components/ui'
import { SEO } from '@/components/seo'

// Type configuration
const TYPE_CONFIG = {
  build: { label: 'Build', icon: '🔨', color: '#14b8a6', bgClass: 'bg-accent-teal/15 text-accent-teal border-accent-teal' },
  fix: { label: 'Fix', icon: '🔧', color: '#f59e0b', bgClass: 'bg-amber-500/15 text-amber-400 border-amber-500' },
  automate: { label: 'Automate', icon: '⚡', color: '#8b5cf6', bgClass: 'bg-violet-500/15 text-violet-400 border-violet-500' },
} as const

type RequestType = keyof typeof TYPE_CONFIG

// Impact configuration
const IMPACT_OPTIONS = [
  { value: 'daily', label: 'Daily', color: 'border-red-500 bg-red-500/20 text-red-400' },
  { value: 'weekly', label: 'Weekly', color: 'border-amber-500 bg-amber-500/20 text-amber-400' },
  { value: 'monthly', label: 'Monthly', color: 'border-accent-teal bg-accent-teal/20 text-accent-teal' },
  { value: 'rarely', label: 'Rarely', color: 'border-gray-500 bg-gray-500/20 text-gray-400' },
]

// Categories
const CATEGORIES = [
  { value: '', label: 'Select a category...' },
  { value: 'Reports', label: 'Reports' },
  { value: 'Client Portal', label: 'Client Portal' },
  { value: 'AI Features', label: 'AI Features' },
  { value: 'Integrations', label: 'Integrations' },
  { value: 'Document Management', label: 'Document Management' },
  { value: 'Client Management', label: 'Client Management' },
  { value: 'Compliance', label: 'Compliance' },
  { value: 'Meetings', label: 'Meetings' },
  { value: 'Other', label: 'Other' },
]

// Existing requests for voting
const EXISTING_REQUESTS = [
  {
    id: '1',
    type: 'build' as RequestType,
    title: 'Bulk document upload with auto-categorisation',
    description: 'Upload multiple client documents at once and have AI automatically categorise and file them.',
    category: 'Document Management',
    upvotes: 47,
  },
  {
    id: '2',
    type: 'automate' as RequestType,
    title: 'Auto-generate annual review reminders',
    description: 'Automatically create tasks and send client reminders when their annual review date approaches.',
    category: 'Client Management',
    upvotes: 35,
  },
  {
    id: '3',
    type: 'build' as RequestType,
    title: 'Integration with Defaqto risk profiler',
    description: 'Connect directly to Defaqto to pull risk profile scores into client records.',
    category: 'Integrations',
    upvotes: 28,
  },
  {
    id: '4',
    type: 'fix' as RequestType,
    title: 'Report generation timeout on large portfolios',
    description: 'When generating reports for clients with 10+ holdings, the process sometimes times out.',
    category: 'Reports',
    upvotes: 23,
  },
]

// Type selector button
function TypeButton({
  type,
  selected,
  onClick,
}: {
  type: RequestType
  selected: boolean
  onClick: () => void
}) {
  const config = TYPE_CONFIG[type]

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 py-3.5 px-4 rounded-md font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 ${
        selected
          ? 'text-brand-dark'
          : 'text-brand-muted hover:text-brand-light'
      }`}
      style={{
        background: selected
          ? `linear-gradient(135deg, ${config.color}, ${config.color}dd)`
          : 'transparent',
      }}
    >
      <span>{config.icon}</span>
      <span>{config.label}</span>
    </button>
  )
}

// Request card for voting
function RequestCard({
  request,
  onUpvote,
  hasVoted,
}: {
  request: (typeof EXISTING_REQUESTS)[0]
  onUpvote: (id: string) => void
  hasVoted: boolean
}) {
  const config = TYPE_CONFIG[request.type]

  return (
    <div className="flex gap-4 bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 transition-all duration-300 hover:border-accent-teal/20">
      {/* Upvote button */}
      <button
        onClick={() => onUpvote(request.id)}
        disabled={hasVoted}
        className={`flex flex-col items-center gap-1 py-3 px-4 rounded-lg border transition-all duration-300 flex-shrink-0 ${
          hasVoted
            ? 'bg-accent-teal/15 border-accent-teal/40 text-accent-teal cursor-default'
            : 'bg-white/[0.03] border-white/[0.08] text-brand-muted hover:border-accent-teal/30 hover:text-accent-teal'
        }`}
      >
        <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 19V5M5 12l7-7 7 7" />
        </svg>
        <span className="text-sm font-semibold">{request.upvotes}</span>
      </button>

      {/* Content */}
      <div className="flex-1">
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold mb-2"
          style={{
            background: `${config.color}15`,
            color: config.color,
          }}
        >
          {config.icon} {config.label}
        </span>
        <h4 className="text-base font-semibold text-brand-light mb-1.5">{request.title}</h4>
        <p className="text-sm text-brand-muted/80 leading-relaxed mb-2">{request.description}</p>
        <span className="text-xs text-brand-subtle uppercase tracking-wider">{request.category}</span>
      </div>
    </div>
  )
}

export default function HelpUsBuildPage() {
  const [formData, setFormData] = useState({
    type: 'build' as RequestType,
    title: '',
    category: '',
    description: '',
    impact: '',
    email: '',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const [requests, setRequests] = useState(EXISTING_REQUESTS)
  const [upvotedIds, setUpvotedIds] = useState<Set<string>>(new Set())

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleUpvote = (id: string) => {
    if (upvotedIds.has(id)) return

    setRequests((prev) =>
      prev.map((req) => (req.id === id ? { ...req, upvotes: req.upvotes + 1 } : req))
    )
    setUpvotedIds((prev) => new Set([...prev, id]))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError(null)

    // Simulate API call
    try {
      await new Promise((resolve) => setTimeout(resolve, 1500))
      setSubmitSuccess(true)
    } catch {
      setSubmitError('Failed to submit request. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReset = () => {
    setFormData({
      type: 'build',
      title: '',
      category: '',
      description: '',
      impact: '',
      email: '',
    })
    setSubmitSuccess(false)
    setSubmitError(null)
  }

  return (
    <>
      <SEO
        title="Help Us Build - WealthSpace | Shape Our Roadmap"
        description="Help shape the future of WealthSpace. Submit feature requests, report issues, or suggest automations. Your ideas drive our roadmap."
        keywords="WealthSpace feedback, feature request, product roadmap, financial adviser software, WealthSpace suggestions"
        url="/help-us-build"
      />

      {/* Hero */}
      <section className="relative py-24 px-6 overflow-hidden">
        {/* Background orbs */}
        <div className="absolute -top-[100px] -right-[100px] w-[400px] h-[400px] bg-[radial-gradient(circle,rgba(20,184,166,0.15)_0%,transparent_70%)] rounded-full" />
        <div className="absolute -bottom-[150px] -left-[100px] w-[300px] h-[300px] bg-[radial-gradient(circle,rgba(139,92,246,0.1)_0%,transparent_70%)] rounded-full" />

        <div className="relative z-10 max-w-[800px] mx-auto text-center">
          <p className="text-[13px] font-semibold text-accent-teal uppercase tracking-[0.1em] mb-4">
            Shape the future
          </p>
          <h1 className="font-serif text-[clamp(2.25rem,5vw,3.25rem)] font-normal leading-[1.1] text-brand-light mb-6">
            Help us <span className="text-accent-teal italic">build</span> WealthSpace
          </h1>
          <p className="text-xl text-brand-muted/80 leading-relaxed max-w-[600px] mx-auto">
            Your ideas drive our roadmap. Vote on features, report issues, or request the automation
            that would make your practice better.
          </p>
        </div>
      </section>

      {/* Submit Form Section */}
      <section className="max-w-[900px] mx-auto px-6 py-16">
        <h2 className="font-serif text-[32px] font-normal text-brand-light mb-3">
          Submit a <span className="text-accent-teal italic">new</span> request
        </h2>
        <p className="text-base text-brand-muted/80 mb-8">
          Got something we haven't thought of? Tell us about it.
        </p>

        <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-8">
          {submitSuccess ? (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-accent-teal/15 flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-accent-teal" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 className="font-serif text-2xl text-brand-light mb-3">Request submitted!</h3>
              <p className="text-base text-brand-muted/80 leading-relaxed mb-6 max-w-md mx-auto">
                Thank you for helping us build a better WealthSpace. We review every submission and
                will reach out if we need more details.
              </p>
              <Button onClick={handleReset}>Submit another request</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Type Selector */}
              <div className="bg-white/[0.03] p-1.5 rounded-[10px] border border-white/[0.06] mb-6 flex gap-2 max-sm:flex-col">
                {(Object.keys(TYPE_CONFIG) as RequestType[]).map((type) => (
                  <TypeButton
                    key={type}
                    type={type}
                    selected={formData.type === type}
                    onClick={() => handleInputChange('type', type)}
                  />
                ))}
              </div>

              {/* Title */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-brand-muted mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Brief summary of your request"
                  required
                />
              </div>

              {/* Category */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-brand-muted mb-2">Category</label>
                <Select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  options={CATEGORIES}
                />
              </div>

              {/* Description */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-brand-muted mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe what you need and why it would help your practice..."
                  rows={4}
                  required
                />
              </div>

              {/* Impact */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-brand-muted mb-2">
                  How often would you use this?
                </label>
                <div className="grid grid-cols-4 max-sm:grid-cols-2 gap-2">
                  {IMPACT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleInputChange('impact', option.value)}
                      className={`py-2.5 px-4 rounded-md border text-sm font-medium transition-all duration-300 ${
                        formData.impact === option.value
                          ? option.color
                          : 'border-white/10 bg-white/[0.03] text-brand-muted hover:border-white/20'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Email */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-brand-muted mb-2">
                  Email <span className="text-brand-subtle font-normal">(optional)</span>
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Get notified when we ship this"
                />
              </div>

              {submitError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-5 text-sm text-red-400">
                  {submitError}
                </div>
              )}

              {/* Submit */}
              <Button type="submit" disabled={isSubmitting} className="w-full justify-center">
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                </svg>
              </Button>
            </form>
          )}
        </div>
      </section>

      {/* Upvote Section */}
      <section className="max-w-[900px] mx-auto px-6 py-16">
        <h2 className="font-serif text-[32px] font-normal text-brand-light mb-3">
          Vote on what <span className="text-accent-teal italic">matters</span> to you
        </h2>
        <p className="text-base text-brand-muted/80 mb-8">
          These requests are already on our radar. Upvote to help us prioritise.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {requests.map((request) => (
            <RequestCard
              key={request.id}
              request={request}
              onUpvote={handleUpvote}
              hasVoted={upvotedIds.has(request.id)}
            />
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[700px] mx-auto px-6 py-16 text-center">
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-12">
          <h2 className="font-serif text-[28px] font-medium text-brand-light mb-4">
            Want to learn more?
          </h2>
          <p className="text-brand-muted/80 mb-6">
            See how WealthSpace is helping advisers transform their practice.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button href="/features">Explore Features</Button>
            <Button href="/contact" variant="secondary">
              Get in Touch
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}
